import React, { useState } from 'react';
import {
    Avatar,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { getFile, pricesPdf } from '../../../../api/customer/files.api';
import { pdf } from '../../../../assets/images';

export const PRICE_PDF_COLUMN_OPTIONS = [
    { key: 'photo', label: 'תמונה' },
    { key: 'barcode', label: 'ברקוד' },
    { key: 'external_id', label: 'מזהה חיצוני' },
    { key: 'internal_id', label: 'מזהה פנימי' },
    { key: 'discount', label: 'הנחה' },
    { key: 'category', label: 'קטגוריה' },
] as const;

export type PricePdfColumnKey = typeof PRICE_PDF_COLUMN_OPTIONS[number]['key'];

const STORAGE_KEY = 'customerPricesPdfColumns';

const DEFAULT_COLS: Record<PricePdfColumnKey, boolean> = {
    photo: false,
    barcode: true,
    external_id: false,
    internal_id: false,
    discount: true,
    category: false,
};

const loadCols = (): Record<PricePdfColumnKey, boolean> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_COLS };
        const parsed = JSON.parse(raw);
        const next = { ...DEFAULT_COLS };
        PRICE_PDF_COLUMN_OPTIONS.forEach(({ key }) => {
            if (typeof parsed?.[key] === 'boolean') next[key] = parsed[key];
        });
        return next;
    } catch {
        return { ...DEFAULT_COLS };
    }
};

export const pricePdfColsParam = (selected: Record<PricePdfColumnKey, boolean>): string => {
    const keys = PRICE_PDF_COLUMN_OPTIONS.filter(({ key }) => selected[key]).map(({ key }) => key);
    return keys.length ? keys.join(',') : 'none';
};

const PricesPdfExport = ({
    clientId,
    clientName,
    date,
    productIds,
    label = 'יצא לPDF',
}: {
    clientId: number;
    clientName: string;
    date: Date;
    productIds?: number[];
    label?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cols, setCols] = useState<Record<PricePdfColumnKey, boolean>>(loadCols);

    const exportPdf = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
        setOpen(false);
        getFile(
            false,
            () => pricesPdf(clientId, date, productIds, pricePdfColsParam(cols)),
            `מחירי מוצרים ${clientName}.pdf`,
            'application/octet-stream',
            undefined,
            setLoading,
        );
    };

    return (
        <>
            <Tooltip title="יצא לPDF">
                <Chip
                    onClick={() => setOpen(true)}
                    icon={loading
                        ? <CircularProgress size={18} />
                        : <Avatar variant="square" sx={{ cursor: 'pointer', bgcolor: 'white', width: 24, height: 24 }} src={pdf} />}
                    label={label}
                    variant="outlined"
                    sx={{
                        cursor: 'pointer',
                        ml: 0.5,
                        mr: 0.5,
                        '& .MuiChip-icon': { color: '#000080' },
                    }}
                />
            </Tooltip>
            <Dialog open={open} onClose={() => !loading && setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>עמודות במחירון</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        שם המוצר והמחיר נכללים תמיד. סמנו עמודות נוספות.
                    </Typography>
                    <FormGroup>
                        {PRICE_PDF_COLUMN_OPTIONS.map(({ key, label: columnLabel }) => (
                            <FormControlLabel
                                key={key}
                                control={(
                                    <Checkbox
                                        checked={cols[key]}
                                        onChange={(event) => setCols((prev) => ({ ...prev, [key]: event.target.checked }))}
                                    />
                                )}
                                label={columnLabel}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={loading}>ביטול</Button>
                    <Button variant="contained" onClick={exportPdf} disabled={loading || !clientId}>
                        יצא לPDF
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PricesPdfExport;
